import React, { useEffect, useRef, useState } from 'react';
import { useStudiesData } from '../../providers/studies/studiesConfig';
import { studiesDefault } from './studiesDefault';
import { useNavigate } from 'react-router';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { getDefaultFilterObj } from '../../providers/studies/studiesConfig';
import { Icon } from '@ohif/ui';
import useWorklistScale from '../WorkListTable/hooks/useWorklistScale';
import StudyShareModal from './studyShareModal';
import { Card } from '../../shared/custom-ui';
import { V2Button } from '../../shared/custom-ui/v2-components';
import UnifiedPagination from '../../shared/custom-ui/UnifiedPagination';
import { ReactComponent as KebabMenu } from '../../../../ui/src/assets/icons/vertical-dots.svg';
import ApiServices from '../../shared/ApiServices/ApiServices';
import ImageUpload from '../../../../app/src/shared/components/ImageUpload/ImageUpload';
import { toast } from 'react-toastify';
import { createPortal } from 'react-dom';
import { useDropdownPosition } from '../../shared/hooks/useDropdownPosition';
import StudiesLayout from './components/StudiesLayout';
import ModernPageHeader from '../WorkListTable/components/ModernPageHeader';
import StudiesDateRangePicker from './components/StudiesDateRangePicker';

interface Study {
    StudyDate: string;
    StudyTime: string;
    StudyInstanceUID: string;
    SK: string;
    [key: string]: any;
}

export default function Studies() {

    // * Studies Context
    const {
        allStudies,
        filterObj,
        setFilterObj,
        getAllStudies,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        studiesLoading,
    } = useStudiesData();
    const { t } = useTranslation();
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

    // Scaling hook for responsive layout
    const containerRef = useRef<HTMLDivElement>(null);
    const scale = useWorklistScale(containerRef, 0.65);

    useEffect(() => {
        console.log('--->', filterObj);
    }, [filterObj]);

    const convertToISO = (dateString: any) => {
        return moment(dateString).toISOString();
    };

    const getStudyDate = (date: string) => {
        return (
            date &&
            moment(date, ['YYYYMMDD', 'YYYY.MM.DD'], true).isValid() &&
            moment(date, ['YYYYMMDD', 'YYYY.MM.DD']).format(t('Common:localDateFormat', 'MMM-DD-YYYY'))
        );
    };

    const getStudyTime = (time: string) =>
        time &&
        moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).isValid() &&
        moment(time, ['HH', 'HHmm', 'HHmmss', 'HHmmss.SSS']).format(
            t('Common:localTimeFormat', 'hh:mm A')
        );

    const ActionMenu = ({
        StudyInstanceUID,
        SK,
        Modality,
        PK,
    }: {
        StudyInstanceUID: string;
        SK: string;
        Modality?: string;
        PK: string;
    }) => {
        const navigate = useNavigate();

        const { isOpen: isDropdownOpen, style: menuStyle, buttonRef, dropdownRef, toggle: toggleDropdown, close: closeDropdown } =
            useDropdownPosition({ boundarySelector: '[data-dropdown-boundary]', onScroll: 'close' });

        useEffect(() => {
            if (!isDropdownOpen) return;
            const handleClickOutside = (event: MouseEvent) => {
                if (
                    dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
                    buttonRef.current && !buttonRef.current.contains(event.target as Node)
                ) {
                    closeDropdown();
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, [isDropdownOpen, closeDropdown, buttonRef, dropdownRef]);

        const showViewer = (target: string) => {
            navigate(`/${target}?StudyInstanceUIDs=${StudyInstanceUID}&studyId=${SK}`);
            closeDropdown();
        };
        const handleDeleteStudy = async () => {
            try {
                await ApiServices.deleteStudy(PK, SK);
                const newFilterObj = {
                    ...filterObj,
                    currentPage: 1,
                    pagination: { prev: null, next: null },
                };
                setFilterObj(newFilterObj);
                await getAllStudies(newFilterObj);
                toast.success('Study deleted successfully');
            } catch (error) {
                console.error('Error deleting study:', error);
                toast.error('Failed to delete study');
            }
            closeDropdown();
        };

        return (
            <div className="relative">
                <button
                    ref={buttonRef}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-[var(--radius-md)] transition-colors focus:outline-none"
                    onClick={toggleDropdown}
                    title="More actions"
                    style={{ color: 'var(--v2-text-secondary)' }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.color = 'var(--v2-text-primary)';
                        e.currentTarget.style.background = 'var(--v2-bg-hover)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.color = 'var(--v2-text-secondary)';
                        e.currentTarget.style.background = 'transparent';
                    }}
                >
                    <KebabMenu className="h-5 w-5" />
                </button>

                {isDropdownOpen && (createPortal(
                    <div
                        ref={dropdownRef}
                        role="menu"
                        className="fixed z-[9999] w-44 overflow-hidden rounded-[var(--radius-lg)] border shadow-lg backdrop-blur-xl"
                        style={{
                            ...menuStyle,
                            background: 'var(--v2-bg-elevated)',
                            borderColor: 'var(--v2-border-subtle)',
                        }}
                    >
                        <button
                            role="menuitem"
                            className="flex w-full items-center gap-2 border-b px-4 py-2.5 text-[13px] font-medium transition-colors"
                            style={{ borderColor: 'var(--v2-border-subtle)', color: 'var(--v2-text-primary)' }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'var(--v2-bg-hover)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            onClick={() => showViewer('viewer')}
                        >
                            <Icon name="eye-visible" className="w-4" /> Viewer
                        </button>
                        <button
                            role="menuitem"
                            className="flex w-full items-center gap-2 border-b px-4 py-2.5 text-[13px] font-medium transition-colors"
                            style={{ borderColor: 'var(--v2-border-subtle)', color: 'var(--v2-text-primary)' }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'var(--v2-bg-hover)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            onClick={() => showViewer('segmentation')}
                        >
                            <Icon name="tab-segmentation" className="w-4" /> Segmentation
                        </button>
                        {Modality === 'SM' && (
                            <button
                                role="menuitem"
                                className="flex w-full items-center gap-2 border-b px-4 py-2.5 text-[13px] font-medium transition-colors"
                                style={{ borderColor: 'var(--v2-border-subtle)', color: 'var(--v2-text-primary)' }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--v2-bg-hover)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                                onClick={() => showViewer('microscopy')}
                            >
                                <Icon name="tab-segmentation" className="w-4" /> Microscopy
                            </button>
                        )}
                        <button
                            role="menuitem"
                            className="flex w-full items-center gap-2 px-4 py-2.5 text-[13px] font-medium transition-colors"
                            style={{ color: 'var(--v2-danger-500)' }}
                            onMouseEnter={e => (e.currentTarget.style.background = 'var(--v2-bg-hover)')}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                            onClick={handleDeleteStudy}
                        >
                            <Icon name="delete-Icon" className="w-4" /> Delete
                        </button>
                    </div>
                    , document.body) as React.ReactNode)}
            </div>
        );
    };

    /** ---------- filters/search ---------- */
    const handleMrnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newFilterObj = {
            ...filterObj,
            mrn: value,
            currentPage: 1,
            pagination: { prev: null, next: null },
        };
        setFilterObj(newFilterObj);
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            getAllStudies(newFilterObj);
        }, 800);
    };

    const handlePatientNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newFilterObj = {
            ...filterObj,
            patientName: value,
            currentPage: 1,
            pagination: { prev: null, next: null },
        };
        setFilterObj(newFilterObj);
        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(() => {
            getAllStudies(newFilterObj);
        }, 800);
    };

    /** Native date input handlers — replaces OHIF DateRange */
    const handleStartDateChange = (val: string) => {
        setStartDate(val as any);
        if (!val && !endDate) return; // both empty, nothing to do
        if (!endDate) return;         // wait for end date before fetching
        finalDateChange({
            startDate: val ? convertToISO(val) : null,
            endDate: endDate ? convertToISO(endDate) : null,
        });
    };

    const handleEndDateChange = (val: string) => {
        setEndDate(val as any);
        finalDateChange({
            startDate: startDate ? convertToISO(startDate) : null,
            endDate: val ? convertToISO(val) : null,
        });
    };

    // ***** FILTER FUNCTIONALITIES (kept) ***** //
    const handleRangeChange = (newRange: number) => {
        const newFilterObj = {
            ...filterObj,
            range: newRange,
            currentPage: 1,
            pagination: { ...getDefaultFilterObj().pagination },
        };
        setFilterObj(newFilterObj);
        getAllStudies(newFilterObj, null);
    };

    const finalDateChange = (dateObj: any) => {
        const newFilterObj = { ...getDefaultFilterObj(), ...dateObj };
        setFilterObj(newFilterObj);
        getAllStudies(newFilterObj, null);
    };

    const tokenChange = (token: string, direction: 'next' | 'prev') => {
        const newFilterObj = {
            ...filterObj,
            currentPage:
                direction === 'next'
                    ? filterObj.currentPage + 1
                    : filterObj.currentPage > 1
                        ? filterObj.currentPage - 1
                        : 1,
            pagination: {
                next: direction === 'next' ? token : null,
                prev: direction === 'prev' ? token : null,
            },
        };
        setFilterObj(newFilterObj);
        getAllStudies(newFilterObj);
    };

    const clearDateFilter = () => {
        const newFilterObj = {
            ...filterObj,
            startDate: null,
            endDate: null,
            pagination: { ...getDefaultFilterObj().pagination },
        };
        setStartDate('' as any);
        setEndDate('' as any);
        setFilterObj(newFilterObj);
        getAllStudies(newFilterObj, null);
    };

    /** Helper for StudiesLayout */
    const formatStudyDate = (row: Study) =>
        `${getStudyDate(row.StudyDate) || ''} ${getStudyTime(row.StudyTime) || ''}`.trim() || '—';

    return (
        <div ref={containerRef} className="relative">
            {/* ══════ HEADER — stays full size for usability ══════ */}
            <ModernPageHeader
                title="Study List"
                subtitle="Filter, search, and open studies. Use each row's menu for viewer options."
                className="overflow-visible"
                actions={
                    <div className="flex items-center gap-2">
                        <ImageUpload />
                    </div>
                }
            >
                <div
                    className="relative rounded-[var(--radius-lg)] border p-4"
                    style={{
                        borderColor: 'var(--border-glass-strong)',
                        background: 'var(--bg-surface)',
                        boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.15)',
                    }}
                >
                    {/* 3-column equal grid — no dangling spacer col */}
                    <div className="grid grid-cols-1 gap-x-5 gap-y-3 md:grid-cols-3 lg:grid-cols-3 lg:items-start">

                        {/* ── Date Range ── */}
                        <div className="flex flex-col gap-1.5">
                            <label
                                className="text-[11px] font-bold uppercase tracking-[0.08em]"
                                style={{ color: 'var(--text-secondary)' }}
                            >
                                Date Range
                            </label>
                            <StudiesDateRangePicker
                                startDate={startDate || ''}
                                endDate={endDate || ''}
                                onStartChange={handleStartDateChange}
                                onEndChange={handleEndDateChange}
                                onClear={clearDateFilter}
                            />
                        </div>

                        {/* ── MRN ── */}
                        <div className="flex flex-col gap-1.5">
                            <label
                                className="text-[11px] font-bold uppercase tracking-[0.08em]"
                                style={{ color: 'var(--text-secondary)' }}
                            >
                                MRN
                            </label>
                            <div className="relative h-10">
                                <Icon
                                    name="icon-search"
                                    className="pointer-events-none absolute left-3 top-1/2 h-[14px] w-[14px] -translate-y-1/2"
                                    style={{ color: 'var(--text-muted)' }}
                                />
                                <input
                                    type="text"
                                    placeholder="Search by MRN..."
                                    value={filterObj.mrn || ''}
                                    onChange={handleMrnChange}
                                    className="h-full w-full rounded-[var(--radius-md)] border pl-9 pr-3 text-[13px] font-medium outline-none transition-all focus:ring-2"
                                    style={{
                                        background: 'var(--bg-card)',
                                        borderColor: 'var(--border-subtle)',
                                        color: 'var(--text-primary)',
                                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
                                    }}
                                />
                            </div>
                        </div>

                        {/* ── Patient Name ── */}
                        <div className="flex flex-col gap-1.5">
                            <label
                                className="text-[11px] font-bold uppercase tracking-[0.08em]"
                                style={{ color: 'var(--text-secondary)' }}
                            >
                                Patient Name
                            </label>
                            <div className="relative h-10">
                                <Icon
                                    name="icon-search"
                                    className="pointer-events-none absolute left-3 top-1/2 h-[14px] w-[14px] -translate-y-1/2"
                                    style={{ color: 'var(--text-muted)' }}
                                />
                                <input
                                    type="text"
                                    placeholder="Search by patient name..."
                                    value={filterObj.patientName || ''}
                                    onChange={handlePatientNameChange}
                                    className="h-full w-full rounded-[var(--radius-md)] border pl-9 pr-3 text-[13px] font-medium outline-none transition-all focus:ring-2"
                                    style={{
                                        background: 'var(--bg-card)',
                                        borderColor: 'var(--border-subtle)',
                                        color: 'var(--text-primary)',
                                        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.04)',
                                    }}
                                />
                            </div>
                        </div>

                    </div>
                </div>
            </ModernPageHeader>

            <div>
                <StudiesLayout
                    data={allStudies}
                    loading={studiesLoading}
                    startIndex={(filterObj.currentPage - 1) * filterObj.range}
                    formatDate={formatStudyDate}
                    // renderShare={(row: Study) => <StudyShareModal studyId={row.SK} />}
                    renderActions={(row: Study) => (
                        <ActionMenu
                            StudyInstanceUID={row.StudyInstanceUID}
                            SK={row.SK}
                            Modality={row.Modality}
                            PK={row.PK}
                        />
                    )}
                    scale={scale}
                />

                {/* PAGINATION */}
                {allStudies && allStudies.length > 0 && (
                    <div data-dropdown-boundary style={{ zoom: scale }}>
                        <div className="mt-4">
                            <UnifiedPagination
                                currentPage={filterObj.currentPage}
                                onPrevious={() => tokenChange(filterObj.pagination?.prev || '', 'prev')}
                                onNext={() => tokenChange(filterObj.pagination?.next || '', 'next')}
                                canGoBack={filterObj.currentPage > 1}
                                hasMore={!!filterObj.pagination?.next}
                                perPage={filterObj.range}
                                perPageOptions={[10, 25, 50, 100]}
                                onPerPageChange={handleRangeChange}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
